import { useEffect, useState } from 'react';
import { Err } from '../ui.js';
import { getProfile, saveProfile, ApiError } from '../api.js';

// WHO IS WRITING. Signature, positioning, and the one-line "what we do".
//
// The send engine already had an identity (which mailbox); it had no author.
// Every draft signed off as whoever set the workspace up, because that was the
// only name in the database. On a fresh install there is no such name — the
// drafts would sign themselves.
//
// Kept to five fields on purpose. This is the smallest set that lets a stranger's
// install write a signed, positioned email; anything more is a profile page.

export function Profile() {
  const [f, setF] = useState({
    name: '', website: '', what_we_do: '', positioning: '',
    operator_name: '', operator_title: '',
  });
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getProfile()
      .then((r) => {
        setF({
          name: r.profile.name ?? '', website: r.profile.website ?? '',
          what_we_do: r.profile.what_we_do ?? '', positioning: r.profile.positioning ?? '',
          operator_name: r.profile.operator_name ?? '',
          operator_title: r.profile.operator_title ?? '',
        });
        setLoaded(true);
      })
      .catch((e) => { setError(e instanceof ApiError ? e.message : String(e)); setLoaded(true); });
  }, []);

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => {
    setF({ ...f, [k]: e.target.value }); setSaved(false);
  };

  async function save() {
    setBusy(true); setError(null);
    try { await saveProfile(f); setSaved(true); }
    catch (e) { setError(e instanceof ApiError ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  if (!loaded) return <section className="panel"><p className="loading">Loading…</p></section>;

  return (
    <section className="panel">
      <h4>Your business</h4>
      <p className="meta">
        The app writes in your name, from this. Blank fields become vague emails.
      </p>

      <Err>{error}</Err>

      <div className="pair">
        <label className="field">
          <span className="field-label">Company</span>
          <input value={f.name} onChange={set('name')} placeholder="Acme Advisory" />
        </label>
        <label className="field">
          <span className="field-label">Website</span>
          <input value={f.website} onChange={set('website')} placeholder="acmeadvisory.com" />
        </label>
      </div>

      <div className="pair">
        <label className="field">
          <span className="field-label">Your name</span>
          <input value={f.operator_name} onChange={set('operator_name')}
            placeholder="Your name" />
        </label>
        <label className="field">
          <span className="field-label">Your title</span>
          <input value={f.operator_title} onChange={set('operator_title')}
            placeholder="Fractional CMO" />
        </label>
      </div>
      <p className="fieldhint">These two sign every email the app sends.</p>

      <label className="field">
        <span className="field-label">What you do, in one line</span>
        <input value={f.what_we_do} onChange={set('what_we_do')}
          placeholder="Fractional CMO for construction software companies" />
      </label>

      <label className="field">
        <span className="field-label">Positioning — why you and not the other option</span>
        <textarea rows={3} value={f.positioning} onChange={set('positioning')}
          placeholder="Operators, not an agency. I sit in your leadership meeting and own the number." />
      </label>

      <div className="actions">
        <button className="primary" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : saved ? 'Saved' : 'Save'}
        </button>
      </div>
    </section>
  );
}
